import React, { useCallback, useState } from 'react';
import { Alert, Button, Col, ListGroup, Row, Stack } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  FileEarmarkFill,
  PencilFill,
  QuestionCircle,
  Trash3Fill,
} from 'react-bootstrap-icons';
import ContentLoader from '../../Components/ContentLoader';
import { READ_INDICATOR } from '../../graphql/queries';
import {type ReadIndicatorQuery } from '../../__generated__/graphql';
import type {
  ExtendedIndicatorAttribute,
  SimpleExample,
} from '../../graphql/types';
import SimpleTooltip from '../../Components/SimpleTooltip';
import { ReturnLayout } from '../../Components/ReturnLayout';
import { ButtonLoading } from '../../Components/ButtonLoading';
import { useMutation } from '@apollo/client';
import { DELETE_EXAMPLE } from '../../graphql/mutations';
import AggregateErrors from '../../utils/AggregateErrors';
import SimpleActionModal from '../../Components/SimpleActionModal';
import SimpleActionTable from '../../Components/SimpleActionTable';

/**
 * Ce composant permet de consulter les détails d'un indicateur. Il
 * est directement accessible à partir de l'URL `/indicateurs/:id`
 * @returns Le composant rendu par React
 */
export function ReadIndicator(): React.ReactElement {
  // L'identifiant unique passé dans l'URL de la page
  const { id } = useParams();

  // Les paramètres de formattage des titres
  const subtitleFormat = 'text-right mb-1 ms-2 fw-bold h4';

  // Variables d'états
  const [exampleToDelete, setExampleToDelete] = useState<SimpleExample>();
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  const [successDeleteMessage, setSuccessDeleteMessage] = useState('');

  // GraphQL
  const [deleteExample, { loading, data, error }] = useMutation(
    DELETE_EXAMPLE,
    { refetchQueries: [READ_INDICATOR] }
  );

  const deleteErrors = AggregateErrors(error, data?.deleteExample.errors);

  // Fonctions de rappel
  /**
   * Fonction de rappel lorsqu'une personne souhaite supprimer un exemple.
   *
   * @param example L'exemple à supprimer.
   */
  const onDeleteExample = (example: SimpleExample): void => {
    setExampleToDelete(example);
    setConfirmModalShow(true);
  };

  /**
   * Fonction de rappel lorsqu'une personne annule la suppression d'un exemple.
   */
  const onClose = useCallback((): void => {
    setConfirmModalShow(false);
    setExampleToDelete(undefined);
  }, []);

  /**
   * Fonction de rappel lorsqu'une personne confirme la suppression d'un exemple.
   */
  const onConfirm = useCallback((): void => {
    setConfirmModalShow(false);
    deleteExample({
      variables: {
        id: exampleToDelete?.id ?? '',
      },
    }).then(result => {
      if (
        (result.data?.deleteExample.errors ?? []).length === 0 &&
        (result.data?.deleteExample.example?.id ?? '') !== ''
      ) {
        setSuccessDeleteMessage("L'exemple a été supprimé avec succès");
        setTimeout(() => {
          setSuccessDeleteMessage('');
        }, 3000);
      }
    });
  }, [exampleToDelete]);

  /**
   * Assure le rendu individuel des inidcateurs
   *
   * @param a L'attribut à rendre
   *
   * @returns L'attribut rendu
   */
  const renderIndicatorAttribute = (
    a: ExtendedIndicatorAttribute
  ): React.ReactElement => {
    return (
      <ListGroup.Item key={a.attribute.id}>
        <div className="d-flex align-items-center">
          <span>
            <a
              className="text-decoration-none link-dark h5"
              href={`/attributs/${a.attribute.id}`}
            >
              {a.attribute.name}
            </a>
          </span>
          <span className="mx-2">
            <SimpleTooltip text={a.attribute.description}>
              <QuestionCircle size={15} />
            </SimpleTooltip>
          </span>
          <small className="fw-light text-mutedf fs-6">
            ({a.attribute.readableValueType})
          </small>
        </div>
        <div>
          <span>{a.primaryValue ?? a.referenceValue?.value}</span>
        </div>
      </ListGroup.Item>
    );
  };

  /**
   * Assure le rendu des attributs pour les indicateurs.
   *
   * @param attributes Les attributs associés à l'indicateur
   *
   * @returns L'indicateur rendu
   */
  const renderIndicatorAttributes = (
    attributes: ExtendedIndicatorAttribute[]
  ): React.ReactElement => (
    <ListGroup>
      {attributes.map<React.ReactElement>(e => renderIndicatorAttribute(e))}
    </ListGroup>
  );

  // La fonction de rappel qui place le contenu
  const displayContent = (
    data: ReadIndicatorQuery | undefined
  ): React.ReactElement => {
    const indicator = data?.indicators?.items?.at(0);
    if (indicator === undefined) {
      return (
        <Alert variant="danger">
          L&apos;identifiant d&apos;indicateur n&apos;est lié à aucun indicateur connu.
        </Alert>
      );
    }

    const exampleTitles = ['Titre du document', 'Actions'];

    const exampleDisposition: Array<
      (item: SimpleExample, index: number) => React.ReactElement
    > = [
      e => <div>{e.document.title}</div>,
      e => (
        <Stack direction="horizontal" gap={1}>
          <Button
            variant="primary"
            href={`/indicateurs/${id}/exemples/${e.id}`}
          >
            <FileEarmarkFill />
          </Button>
          {e.canBeEdited && (
            <Button
              variant="primary"
              href={`/indicateurs/${id}/exemples/${e.id}/modification`}
            >
              <PencilFill />
            </Button>
          )}
          {e.canBeDeleted && (
            <SimpleTooltip text="Supprimer l'indicateur">
              <ButtonLoading
                variant="danger"
                // skipcq JS-0417
                onClick={() => {
                  onDeleteExample(e);
                }}
                loading={e.id === exampleToDelete?.id && loading}
                testid={`delete-example-${e.id}`}
              >
                <Trash3Fill />
              </ButtonLoading>
            </SimpleTooltip>
          )}
        </Stack>
      ),
    ];

      return (
        <>
          <Row className="my-3">
            <Col>
              <div className="h1">Détail d&apos;un indicateur</div>
            </Col>
          </Row>
          {/* Attributs obligatoires */}
          <Row>
            <Col>
              <div>
                <p className={subtitleFormat}>Attributs principaux</p>
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              {renderIndicatorAttributes(
                indicator.attributes
                  .filter(e => e.attribute.mandatory)
                  .filter((e, i) => i % 2 === 0)
              )}
            </Col>
            <Col>
              {renderIndicatorAttributes(
                indicator.attributes
                  .filter(e => e.attribute.mandatory)
                  .filter((e, i) => i % 2 === 1)
              )}
            </Col>
          </Row>
          {/* Attributs optionnels */}
          {indicator.attributes.filter(e => !e.attribute.mandatory).length >
            0 && (
            <div>
              <p className={subtitleFormat}>Attributs additionnels</p>
            </div>
          )}
          <Row className="mb-1">
            <Col>
              {renderIndicatorAttributes(
                indicator.attributes
                  .filter(e => !e.attribute.mandatory)
                  .filter((e, i) => i % 2 === 0)
              )}
            </Col>
            <Col>
              {renderIndicatorAttributes(
                indicator.attributes
                  .filter(e => !e.attribute.mandatory)
                  .filter((e, i) => i % 2 === 1)
              )}
            </Col>
          </Row>
          {/* Métadonnées */}
          <Row>
            <Col>
              <hr />
            </Col>
          </Row>
          <Row>
            <Col>
              <div>
                <p className={subtitleFormat}>Métadonnées</p>
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="auto">
              <ListGroup>
                <ListGroup.Item>
                  Créé le : {new Date(indicator.createdOn).toLocaleString()}
                </ListGroup.Item>
                <ListGroup.Item>
                  Modifié le : {new Date(indicator.editedOn).toLocaleString()}
                </ListGroup.Item>
                <ListGroup.Item>
                  Créateur : {indicator.owner.firstName}{' '}
                  {indicator.owner.lastName}
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          {/* Exemples */}
          <Row>
            <Col>
              <hr />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={9}>
              <p className={subtitleFormat}>Exemples</p>
            </Col>
            <Col md={3}>
              <div className="d-grid">
                <Button
                  variant="success"
                  href={`/indicateurs/${id}/exemples/ajout`}
                >
                  Nouveau
                </Button>
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <SimpleActionTable<SimpleExample>
                titles={exampleTitles}
                disposition={exampleDisposition}
                data={indicator.examples ?? []}
                key={(indicator.examples ?? []).length}
              />
            </Col>
          </Row>
        </>
      );
  };

  return (
    <ReturnLayout href="/indicateurs">
      <Row>
        <Col>
          <ContentLoader
            query={READ_INDICATOR}
            variables={{ indicatorId: id ?? '' }}
            data={displayContent}
          />
        </Col>
      </Row>
      <SimpleActionModal
        successMessage={successDeleteMessage}
        errorMessages={deleteErrors}
        genericErrorMessage="Des erreurs sont survenues lors de la suppression de l'exemple"
        show={confirmModalShow}
        onClose={onClose}
        onConfirm={onConfirm}
        title="Confirmer la suppression"
        confirmButtonText="Supprimer l'exemple"
      >
        <>
          <p>Êtes-vous certain(e) de vouloir supprimer l&apos;exemple?</p>
          <p>L&apos;action est irréversible.</p>
        </>
      </SimpleActionModal>
    </ReturnLayout>
  );
}
